import { useState } from "react";
import { OrderItem } from "../models/IOrders";
import { parseMenuExtras } from "../utils/Util";
import {
  callPrintServer, discoverPrinters, isPrinterConfigured, loadPrinterSettings, PrinterSettings, printerPayload, savePrinterSettings,
} from "../utils/printerSettings";
import { buildReceiptBytes, isAndroid, printViaRawBT } from "../utils/escpos";
import PrinterSettingsModal from "./PrinterSettingsModal";

/**
 * Yazıcı hiç ayarlanmamışsa otomatik bulmayı dener: tek aday (USB ya da ağ) varsa onu kaydeder.
 * Birden fazla ya da hiç yoksa null — kullanıcıya ayar penceresi açılır.
 */
async function autoConfigure(current: PrinterSettings): Promise<PrinterSettings | null> {
  // Android tablette yerel program çalışmaz; fiş RawBT uygulamasıyla basılır
  if (isAndroid()) {
    const next: PrinterSettings = { ...current, connection: "rawbt" };
    savePrinterSettings(next);
    return next;
  }
  const r = await discoverPrinters(current);
  if (!r.ok) return null;
  const usb = r.windowsPrinters.filter((w) => w.usb);
  let next: PrinterSettings | null = null;
  if (usb.length === 1 && r.printers.length === 0) next = { ...current, connection: "windows", printerName: usb[0].name };
  else if (r.printers.length === 1 && usb.length === 0) next = { ...current, connection: "network", host: r.printers[0].host, port: r.printers[0].port };
  if (next) savePrinterSettings(next);
  return next;
}

interface PrintReceiptProps {
  order: OrderItem;
  restaurantName: string;
}

export const PrintReceipt = ({ order, restaurantName }: PrintReceiptProps) => {
  const [printing, setPrinting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  // Yazıcı tanımlı değilken basılırsa önce ayarlar açılır, kaydedilince yazdırma sürer
  const [printAfterSave, setPrintAfterSave] = useState(false);

  const buildPayload = () => {
    const items = order.orderDetail?.foodPreOrder?.map((food) => {
      const extras = parseMenuExtras(food.menuUpgradeNotes).filter((e) => e.quantity > 0);
      // Siparişteki (kampanyalı) birim fiyat; yoksa menü fiyatı
      const price = food.discountedUnitPrice || food.unitPrice || food.food.foodPrice;
      return {
        name: food.food.foodNameTr,
        qty: food.foodQuantity,
        price,
        note: food.foodOrderNote,
        extras: extras.map((e) => ({ name: e.name, qty: e.quantity, totalQty: food.foodQuantity * e.quantity, total: e.total })),
      };
    }) ?? [];

    const a = order.customerAddress;
    const c = order.preOrder?.customer;
    const lat = Number(a?.customerAddressLatitude);
    const lng = Number(a?.customerAddressLongitude);
    const hasLocation = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
    return {
      restaurantName,
      orderId: order.orderNameUniqe,
      orderDate: order.orderDate,
      address: a ? `${a.customerAddressDescription ?? ""} ${a.city?.cityNameTr ?? ""}/${a.town?.townNameTr ?? ""}`.trim() : "",
      // Fişin altındaki QR kodu — kurye okutunca Google Haritalar'da adres açılır ("Haritada Gör" ile aynı link)
      mapUrl: hasLocation ? `https://www.google.com/maps?q=${lat},${lng}` : undefined,
      customerName: c ? `${c.customerName ?? ""} ${c.customerSurName ?? ""}`.trim() : "",
      customerPhone: c?.customerTelNo ?? "",
      paymentMethod: order.paymentMethod?.paymentMethodNameTr ?? "",
      discount: order.discountAmount ?? 0,
      items,
      total: order.orderPrice,
      orderNote: order.orderNote,
    };
  };

  const print = async (initial: PrinterSettings = loadPrinterSettings()) => {
    let settings = initial;
    setPrinting(true);
    setResult(null);
    if (!isPrinterConfigured(settings)) {
      // İlk kullanım: yazıcıyı kendimiz bulmayı dene; tek yazıcı varsa kimseye bir şey sormadan bas
      const auto = await autoConfigure(settings);
      if (!auto) {
        setPrinting(false);
        setPrintAfterSave(true);
        setShowSettings(true);
        return;
      }
      settings = auto;
    }

    if (settings.connection === "rawbt") {
      // Fiş burada hazırlanır, RawBT uygulaması ağdaki yazıcıya basar
      const one = buildReceiptBytes(buildPayload(), {
        // Resim olarak basma yalnız yazdırma programında var; RawBT'de Türkçe tabloyla basılır
        width: settings.width, charset: settings.charset === "image" ? "tr" : settings.charset, codePage: settings.codePage, cut: settings.cut,
      });
      const copies = Math.max(1, Math.min(5, settings.copies || 1));
      const all = new Uint8Array(one.length * copies);
      for (let i = 0; i < copies; i++) all.set(one, i * one.length);
      printViaRawBT(all);
      setPrinting(false);
      setResult({ ok: true, text: "Fiş RawBT uygulamasına gönderildi." });
      setTimeout(() => setResult(null), 3000);
      return;
    }

    const r = await callPrintServer("/api/print", { ...buildPayload(), printer: printerPayload(settings) }, settings);
    setResult({ ok: r.ok, text: r.ok ? "Fiş yazdırıldı." : r.message });
    setPrinting(false);
    if (r.ok) setTimeout(() => setResult(null), 3000);
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 6 }}>
        <button className="btn btn-sm btn-outline-dark" style={{ flex: 1 }} onClick={() => print()} disabled={printing}>
          {printing
            ? <><span className="spinner-border spinner-border-sm me-1" />Yazdırılıyor...</>
            : <><i className="bi bi-printer me-1"></i> Fişi Yazdır</>}
        </button>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => { setPrintAfterSave(false); setShowSettings(true); }}
          title="Yazıcı ayarları" aria-label="Yazıcı ayarları">
          <i className="bi bi-gear"></i>
        </button>
      </div>

      {result && (
        <div style={{
          marginTop: 6, fontSize: ".76rem", borderRadius: 8, padding: "6px 9px", display: "flex", gap: 6, alignItems: "flex-start",
          background: result.ok ? "#f0fdf4" : "#fef2f2", color: result.ok ? "#15803d" : "#b91c1c",
        }}>
          <i className={`bi ${result.ok ? "bi-check-circle-fill" : "bi-exclamation-circle-fill"}`} style={{ marginTop: 1 }}></i>
          <span style={{ flex: 1 }}>{result.text}</span>
          {!result.ok && (
            <button type="button" onClick={() => setShowSettings(true)}
              style={{ background: "none", border: "none", padding: 0, color: "inherit", fontWeight: 700, textDecoration: "underline", cursor: "pointer", whiteSpace: "nowrap" }}>
              Ayarlar
            </button>
          )}
        </div>
      )}

      {showSettings && (
        <PrinterSettingsModal
          onClose={() => setShowSettings(false)}
          onSaved={(s) => { if (printAfterSave) { setPrintAfterSave(false); print(s); } }}
        />
      )}
    </div>
  );
};
